import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import Icon from '../../ui/Icon/Icon'
import TextInput from '../../ui/TextInput/TextInput'
import DialogModal from '../../ui/DialogModal/DialogModal'

import NewSideBarBG from '../../../assets/img/sidebar-bgnew.webp'

import getDeviceDistance from '../../../services/location/getDeviceDistance'
import formatDistance from '../../../services/data/formatDistance'
import { setGAEvent } from '../../../services/analytics/google-analytics'

const SearchMallContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  background: #ffffff;
`

const Header = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-height: 87px;
  font-size: 0.7rem;
  z-index: 100;

  background-image: url('${NewSideBarBG}'), linear-gradient(var(--color-primary), var(--color-primary));
  background-position: right top;
  background-size: 100%;
`

const CloseIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 1.5rem;
  margin-left: 20px !important;
  margin-right: 20px !important;
  color: var(--color-fg-light);
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;

  & > svg {
    position: relative;
    color: #ffffff;
  }
`

const CloseIconFiller = styled.span`
  display: flex;
  width: 2rem;
  margin-left: 20px;
  margin-right: 20px;
`

const Title = styled.h1`
  font-family: var(--font-family-ayala-display-sans);
  flex: 1;
  padding: 20px 0;
  margin: 0;
  margin-left: 60px;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: var(--font-weight-boldest);
  text-transform: uppercase;
  text-align: center;
`

const Content = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`

const SearchFieldsContainer = styled.div`
  margin: 1rem;
  background: #ffffff;
`

const SearchResults = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  flex: 1;
`

const SectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-narrow);
  text-transform: uppercase;
  color: var(--color-primary);
  padding: 0 1.25rem;
  margin-bottom: 0.5rem;
`

const Tooltip = styled.span`
  position: absolute;
  top: 33px;
  left: 81px;
  visibility: hidden;
  font-size: 0.75rem;
  background-color: black;
  color: #ffffff;
  text-align: center;
  border-radius: 6px;
  width: 200px;
  padding: 5px 0;
  z-index: 1;
`

const Result = styled.li`
  position: relative;
  display: flex;
  padding: 1rem 1.25rem;
  border-top: 1px solid #f1f1f1;
  align-items: center;
  max-height: 330px;
  user-select: none;
  cursor: pointer;

  &:last-child {
    border-bottom: 1px solid #f1f1f1;
  }

  &:hover {
    ${Tooltip} {
      visibility: visible;
    }
  }
`

const StarIcon = styled.span`
  display: flex;
  color: ${props =>
    props.favorite ? props.colorVariant : 'var(--color-fg-lighter)'};
  font-size: 1.5rem;
`

const ResultText = styled.div`
  font-size: 0.9375rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-narrow);
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 1.5rem;
`

const Distance = styled.span`
  font-size: 0.75rem;
`

const ComingSoon = styled.span`
  color: var(--color-primary);
  font-size: 0.5rem;
  line-height: 1rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.4px;
  background: #eaf1f9;
  border-radius: var(--border-radius-small);
  padding: 3px 8px;
  white-space: nowrap;
  text-transform: uppercase;
`

const FavoriteMallList = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 1rem 1.25rem 2rem;
  overflow: hidden;
  justify-content: space-between;
`

const FavoriteMallCard = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex: 0 0 auto;
  height: 104px;
  width: 104px;
  border-radius: var(--border-radius-card);
  background-size: cover;
  background-position: center;
  ${props => {
    if (!props.empty) {
      return css`
        cursor: pointer;
        background: linear-gradient(rgba(64,69,104,0.70), rgba(64,69,104,0.70)), url('${props.background}');`
    }
    return null
  }}

  &:first-child {
    margin-left: 0;
  }

  &:hover {
    ${() => css`
      ${Tooltip} {
        visibility: visible;
      }
    `}
  }
`

const MallIcon = styled.div.attrs(props => ({
  style: { WebkitMaskImage: `url('${props.image}')` },
}))`
  height: 70px;
  width: 70px;
  background-color: #ffffff;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
`

const RemoveFavoriteButton = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 1rem;
  color: #5bbeaf;
  background: #ffffff;
  box-shadow: var(--box-shadow);
  z-index: 1;

  & > svg {
    position: relative;
    color: #5bbeaf;
  }
`

class SearchMall extends React.Component {
  constructor(props) {
    super(props)

    const { favoriteMalls } = props

    const favoriteMallList = favoriteMalls.map(favMall => ({
      ...favMall,
      fromFavorites: true,
    }))

    this.currentPage = React.createRef()

    const mallsWithFavorite = this.updateMallsWithFavorite(favoriteMalls)

    this.state = {
      mallList: mallsWithFavorite,
      searchResult: mallsWithFavorite,
      favoriteMalls: favoriteMallList,
      errorMessage: '',
      isUserSearching: false,
    }
  }

  componentDidMount() {
    this.currentPage.current.addEventListener('scroll', this.handleScroll)
  }

  componentDidUpdate(prevProps) {
    const { malls, favoriteMalls } = this.props

    if (
      prevProps.favoriteMalls !== favoriteMalls ||
      prevProps.malls !== malls
    ) {
      const favoriteMallList = favoriteMalls.map(favMall => ({
        ...favMall,
        fromFavorites: true,
      }))

      const mallsWithFavorite = this.updateMallsWithFavorite(favoriteMalls)

      this.updateMallListState({
        mallsWithFavorite,
        favoriteMallList,
      })
    }
  }

  componentWillUnmount() {
    this.currentPage.current.removeEventListener('scroll', this.handleScroll)
  }

  updateMallListState = ({ mallsWithFavorite, favoriteMallList }) => {
    this.setState({
      mallList: mallsWithFavorite,
      searchResult: mallsWithFavorite,
      favoriteMalls: favoriteMallList,
    })
  }

  updateMallsWithFavorite = favorite => {
    const { malls } = this.props

    const favoriteMallIds = favorite.map(fav => fav.id)

    return malls.map(mall => {
      if (favoriteMallIds.includes(mall.id)) {
        return {
          ...mall,
          fromFavorites: true,
        }
      }

      return mall
    })
  }

  handleFavoriteToggle = async (e, data) => {
    e.stopPropagation()

    e.preventDefault()

    const { id, fromFavorites, name } = data
    const {
      onFavoriteClicked,
      setFavoriteMalls,
      accessToken,
      favoriteMalls,
    } = this.props

    if (onFavoriteClicked) {
      try {
        let favoriteResponseState

        if (accessToken) {
          const favoriteMallsIds = favoriteMalls.map(m => m.id)
          if (favoriteMallsIds.includes(id)) {
            favoriteResponseState = favoriteMalls.filter(m => m.id !== id)
          } else {
            favoriteResponseState = [
              ...favoriteMalls,
              {
                ...data,
                favoritedAt: new Date(),
                fromFavorites: true,
              },
            ]
          }

          setFavoriteMalls(favoriteResponseState)
        }

        const favoriteResponse = await onFavoriteClicked(id)

        if (accessToken) {
          if (!fromFavorites) {
            setGAEvent('mall', 'favorite_mall', name)
          }

          const favoriteMallIds = favoriteResponse.map(fav => fav.id)

          const newFavoriteMalls = favoriteMalls.filter(
            mall => favoriteMallIds.indexOf(mall.id) >= 0,
          )
          const mallsWithFavorite = this.updateMallsWithFavorite(
            favoriteResponse,
          )

          setFavoriteMalls(favoriteResponse)

          this.setState({
            mallList: mallsWithFavorite,
            searchResult: mallsWithFavorite,
            favoriteMalls: newFavoriteMalls,
          })
        }
      } catch (error) {
        setFavoriteMalls(favoriteMalls)
        this.setState({
          errorMessage: error.message,
        })
      }
    }
  }

  handleSearch = e => {
    const { mallList } = this.state

    const search = e.target.value.toLowerCase()

    const result = mallList.filter(mall => {
      const mallName = mall.name.replace(/[^\w]/gi, '')

      return (
        mall.name.toLowerCase().includes(search) ||
        mallName.toLowerCase().includes(search)
      )
    })

    this.setState({
      searchResult: result,
      isUserSearching: e.target.value !== '',
    })
  }

  displayList = (
    { id, name, fromFavorites, distance, explore, enableFavorite },
    index,
  ) => {
    const { onSelectMall, efood } = this.props
    const colorIndex = index % 3
    const colorVariants = ['#EB7B89', '#5BBEAF', '#F5BE2B']

    return (
      <Result
        key={id}
        onClick={() => (explore || efood ? onSelectMall(id) : null)}
      >
        {!explore && !efood && (
          <Tooltip>Zing it soon at other Ayala Malls!</Tooltip>
        )}
        <StarIcon
          favorite={fromFavorites}
          onClick={e =>
            explore && enableFavorite
              ? this.handleFavoriteToggle(e, { id, fromFavorites, name })
              : e.stopPropagation()
          }
          colorVariant={colorVariants[colorIndex]}
        >
          <Icon name="star" />
        </StarIcon>
        <ResultText>{name}</ResultText>
        {!explore && !efood ? (
          <ComingSoon>Coming Soon</ComingSoon>
        ) : (
          <>{!!distance && <Distance>{formatDistance(distance)}</Distance>}</>
        )}
      </Result>
    )
  }

  displayFavorite = f => {
    if (f === null) {
      return <FavoriteMallCard empty />
    }

    const { iconUrl, id, fromFavorites, favorite, photos, explore } = f

    const { onSelectMall, efood } = this.props
    const backgroundPhoto =
      photos && photos.length && photos[0] ? photos[0].url : ''

    return (
      <FavoriteMallCard
        background={backgroundPhoto}
        onClick={() => (explore || efood ? onSelectMall(id) : null)}
      >
        {/* {(!explore || !efood) && (
          <Tooltip>Zing it soon at other Ayala Malls!</Tooltip>
        )} */}
        <MallIcon image={iconUrl} />
        {!favorite && (
          <RemoveFavoriteButton
            onClick={e => this.handleFavoriteToggle(e, { id, fromFavorites })}
          >
            <Icon name="x" />
          </RemoveFavoriteButton>
        )}
      </FavoriteMallCard>
    )
  }

  toggleDialogModal = show => {
    this.setState({
      errorMessage: show || '',
    })
  }

  sorter = (a, b) => a.name.localeCompare(b.name)

  sortActive = (a, b) => (a.explore === b.explore ? 0 : a.explore ? -1 : 1)

  sortNearest = (a, b) => {
    return a.distance === b.distance ? 0 : a.distance < b.distance ? -1 : 1
  }

  limitMalls = (mall, index) => {
    return index < 3
  }

  handleScroll = event => {
    const { hasScrolled: hasScrolledState } = this.state
    const { scrollTop } = event.target
    const hasScrolled = scrollTop > 0

    if (hasScrolled !== hasScrolledState) {
      this.setState({
        hasScrolled,
      })
    }
  }

  render() {
    const {
      searchResult,
      errorMessage,
      isUserSearching,
      hasScrolled,
    } = this.state
    const {
      onPageClose,
      favoriteMalls,
      malls,
      deviceLocation,
      onSelectMall,
      displayClose = true,
      onFavoriteClicked,
      efood,
      // displaySelectAll = true,
      ...restProps
    } = this.props

    const faveMallsId = favoriteMalls.map(mall => mall.id)

    const hasForceFavorite = malls.find(mall => mall.favorite)

    let favorite = 0

    const searchResultFiltered = searchResult
      .map(mall => ({
        ...mall,
        fromFavorites: mall.favorite || faveMallsId.includes(mall.id),
        enableFavorite: !hasForceFavorite,
      }))
      .sort(this.sorter)
      .sort((a, b) => (a.favorite === b.favorite ? 0 : a.favorite ? 1 : -1))
      .map(mall => {
        if (mall.fromFavorites) {
          favorite++
        }

        return {
          ...mall,
          displayFavorite: favorite <= 3 ? mall.fromFavorites : false,
        }
      })

    const mallsWithDistance =
      deviceLocation && !efood
        ? searchResultFiltered.map(mall => ({
            ...mall,
            distance: getDeviceDistance(
              {
                lat: deviceLocation.latitude,
                lon: deviceLocation.longitude,
              },
              {
                lat: mall.latitude,
                lon: mall.longitude,
              },
            ),
          }))
        : searchResultFiltered

    const nearestMalls = efood
      ? mallsWithDistance
      : mallsWithDistance.filter(
          mallWithDistance => mallWithDistance.distance <= 5000,
        )

    return (
      <SearchMallContainer ref={this.currentPage} {...restProps}>
        <Header hasScrolled={hasScrolled}>
          <Title>Select Mall</Title>
          {displayClose ? (
            <CloseIcon onClick={onPageClose}>
              <Icon name="x" />
            </CloseIcon>
          ) : (
            <CloseIconFiller />
          )}
        </Header>
        <Content onFavoriteClicked={onFavoriteClicked}>
          <SearchFieldsContainer>
            <TextInput
              name="keyword"
              variant="withIcon"
              iconName="search"
              onChange={this.handleSearch}
              placeholder="Search Mall"
              autoComplete="off"
            />
          </SearchFieldsContainer>
          {mallsWithDistance.filter(mall => mall.displayFavorite).length >
            0 && (
            <>
              <SectionTitle>Favorites</SectionTitle>
              <FavoriteMallList>
                {[
                  ...mallsWithDistance.filter(mall => mall.displayFavorite),
                  null,
                  null,
                ]
                  .slice(0, 3)
                  .map(this.displayFavorite)}
              </FavoriteMallList>
            </>
          )}
          {(nearestMalls.length && !isUserSearching && !efood) > 0 && (
            <>
              <SectionTitle>Nearest Mall/s</SectionTitle>
              <SearchResults>
                {nearestMalls
                  .sort(this.sorter)
                  .sort(this.sortActive)
                  .sort(this.sortNearest)
                  .filter(this.limitMalls)
                  .map(this.displayList)}
              </SearchResults>
            </>
          )}
          <SectionTitle>
            {isUserSearching ? 'Results' : 'All Malls'}
          </SectionTitle>
          <SearchResults>
            {efood
              ? searchResultFiltered
                  .filter(mall => {
                    return isUserSearching || !mall.displayFavorite
                  })
                  .sort(this.sortNearest)
                  .map(this.displayList)
              : searchResultFiltered
                  .filter(mall => {
                    return isUserSearching || !mall.displayFavorite
                  })
                  .sort(this.sortNearest)
                  .sort(this.sortActive)
                  .map(this.displayList)}
          </SearchResults>
        </Content>
        <DialogModal
          modalState={!!errorMessage}
          title="Favorite Malls"
          description={errorMessage}
          okText="Okay"
          onOk={() => this.toggleDialogModal(false)}
          hasCancel={false}
          align="center"
        />
      </SearchMallContainer>
    )
  }
}

SearchMall.defaultProps = {
  malls: [],
  favoriteMalls: [],
}

SearchMall.propTypes = {
  malls: PropTypes.array,
  favoriteMalls: PropTypes.array,
}

export default SearchMall
